// Thin fetch wrappers for the Worker API.
// In dev, Vite proxies /api and /oauth to the Worker on :8787.
// In prod, same-origin (Pages + Worker under one hostname).

import { accessToken, showAuthMessage, localSignOut, markSessionHealthy } from './auth.js';

const BASE = ''; // same-origin

async function req(path, init = {}) {
  const token = await accessToken();
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
    credentials: 'include',
  });
  if (res.status === 403) {
    showAuthMessage('This account isn’t allowed in HQ.');
    throw new Error('HQ access forbidden');
  }
  if (res.status === 401) {
    // Session revoked/expired (e.g. signed out from the estimator): drop it
    // locally, then reload so the gate re-runs the /sso handoff. The gate's
    // attempt counter is only reset by a successful gated call
    // (markSessionHealthy below), so a session the Worker keeps rejecting
    // stops after 2 handoffs with the "Couldn't sign you in" message.
    await localSignOut();
    location.reload();
    throw new Error('HQ session expired');
  }
  if (res.status === 503) {
    // Worker couldn't reach Supabase to check the token (outage / rate
    // limit). The session may be fine: keep it and don't reload.
    throw new Error(`API 503 auth unavailable — ${path}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status} ${res.statusText} — ${path} — ${text}`);
  }
  if (path !== '/api/health') markSessionHealthy(); // /api/health is ungated
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  // ---- prefs ----
  listPrefs: () => req('/api/prefs'),
  putPref: (key, value) =>
    req('/api/prefs/' + encodeURIComponent(key), {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),
  deletePref: (key) =>
    req('/api/prefs/' + encodeURIComponent(key), { method: 'DELETE' }),

  // ---- custom panels ----
  listPanels: () => req('/api/custom-panels'),
  createPanel: (panel) =>
    req('/api/custom-panels', { method: 'POST', body: JSON.stringify(panel) }),
  updatePanel: (id, patch) =>
    req('/api/custom-panels/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  deletePanel: (id) =>
    req('/api/custom-panels/' + encodeURIComponent(id), { method: 'DELETE' }),

  // ---- custom quadrants ----
  listQuadrants: () => req('/api/custom-quadrants'),
  createQuadrant: (quad) =>
    req('/api/custom-quadrants', { method: 'POST', body: JSON.stringify(quad) }),
  updateQuadrant: (id, patch) =>
    req('/api/custom-quadrants/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  deleteQuadrant: (id) =>
    req('/api/custom-quadrants/' + encodeURIComponent(id), { method: 'DELETE' }),

  // ---- notes ----
  listNotes: () => req('/api/notes'),
  createNote: (note) =>
    req('/api/notes', { method: 'POST', body: JSON.stringify(note) }),
  deleteNote: (id) =>
    req('/api/notes/' + encodeURIComponent(id), { method: 'DELETE' }),

  // ---- meta ----
  whoami: () => req('/api/whoami'),
  health: () => req('/api/health'),
};
